import {
  Dialog as ChakraDialog,
  Portal,
  type DialogBackdropProps,
} from "@chakra-ui/react";

import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import CloseButton, {
  type CloseButtonProps as UiKitCloseButtonProps,
} from "@ui-kit/form/CloseButton/CloseButton.web";
import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  type ComponentProps,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import { useTranslation } from "@shared/utils/internationalization";
import type {
  ModalBodySharedProps,
  ModalHeaderSharedProps,
  ModalSharedProps,
} from "./Modal.props";

type ChakraDialogRootProps = ComponentProps<typeof ChakraDialog.Root>;
type ChakraDialogContentProps = ComponentProps<typeof ChakraDialog.Content>;
type ChakraDialogHeaderProps = ComponentProps<typeof ChakraDialog.Header>;
type ChakraDialogBodyProps = ComponentProps<typeof ChakraDialog.Body>;
type ChakraDialogFooterProps = ComponentProps<typeof ChakraDialog.Footer>;
type ChakraDialogCloseTriggerProps = ComponentProps<
  typeof ChakraDialog.CloseTrigger
>;

type ModalBodyProps = ChakraDialogBodyProps & ModalBodySharedProps;

export type { ModalBodyProps };

export type ModalProps = ModalSharedProps &
  Omit<
    ChakraDialogRootProps,
    | "children"
    | "open"
    | "initialFocusEl"
    | "finalFocusEl"
    | "onOpenChange"
    | "scrollBehavior"
    | "closeOnEscape"
    | "closeOnInteractOutside"
    | "preventScroll"
    | "placement"
  > & {
    /** Chakra v2+ forward-compat */
    open?: boolean;
    /** Chakra v1 */
    isCentered?: boolean;
    /** Chakra v2+ */
    centered?: boolean;
    /** Chakra v1 */
    closeOnEsc?: boolean;
    /** Chakra v2+ */
    closeOnEscape?: boolean;
    /** Chakra v1 */
    closeOnOverlayClick?: boolean;
    /** Chakra v3 */
    closeOnInteractOutside?: boolean;
    /** Chakra v1 */
    blockScrollOnMount?: boolean;
    /** Chakra v3 */
    preventScroll?: boolean;
    /** Chakra v3 */
    placement?: ChakraDialogRootProps["placement"];
    /** Chakra v3 */
    scrollBehavior?: ChakraDialogRootProps["scrollBehavior"];
  };

type ModalType = ModalProps & {
  children: ReactNode;
  modalBodyProps?: ModalBodyProps & {
    "data-testid"?: string;
    style?: CSSProperties;
  };
  modalContentProps?: ChakraDialogContentProps & {
    "data-testid"?: string;
  };
};

export const bodyBackgroundColor = (variant: ModalProps["variant"]) => {
  switch (variant) {
    case "informational":
      return "bg";
    case "interactive":
      return "bg.subtle";
    default:
      return "bg";
  }
};

function extractBaseSlots(children: ReactNode): {
  overlay: ReactNode | null;
  content: ReactElement | null;
  otherChildren: ReactNode[];
} {
  const otherChildren: Array<ReactNode> = [];
  let overlay: ReactNode | null = null;
  let content: ReactElement | null = null;

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      otherChildren.push(child);
      return;
    }

    if (child.type === Modal.Overlay) {
      overlay = child;
      return;
    }

    if (child.type === Modal.Content) {
      content = child as ReactElement;
      return;
    }

    otherChildren.push(child);
  });

  return { overlay, content, otherChildren };
}

function extractSlots(children: ReactNode) {
  const otherChildren: Array<ReactNode> = [];
  let footer: ReactNode | null = null;
  let header: ReactNode | null = null;
  let overlay: ReactNode | null = null;
  let body: ReactNode | null = null;

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      otherChildren.push(child);
    } else if (child.type === Modal.Footer) {
      footer = child;
    } else if (child.type === Modal.Header) {
      header = child;
    } else if (child.type === Modal.Overlay) {
      overlay = child;
    } else if (child.type === Modal.Body) {
      body = child;
    } else {
      otherChildren.push(child);
    }
  });

  return [overlay, header, body, footer, otherChildren] as const;
}

export default function Modal({
  children,
  modalContentProps = {},
  initialFocusRef,
  finalFocusRef,
  variant = "informational",
  onClose,
  hideOverlay = false,
  scrollBehaviour = "outside",
  isOpen,
  open,
  isCentered,
  centered,
  closeOnEsc,
  closeOnEscape,
  closeOnOverlayClick,
  closeOnInteractOutside,
  blockScrollOnMount,
  preventScroll,
  placement,
  scrollBehavior,
  onOpenChange,
  size,
  ...rest
}: ModalType): ReactElement {
  const openValue = open ?? isOpen;
  const centeredValue = centered ?? isCentered;
  const closeOnEscValue = closeOnEscape ?? closeOnEsc;
  const closeOnOverlayValue =
    closeOnInteractOutside ?? closeOnOverlayClick ?? true;
  const preventScrollValue = preventScroll ?? blockScrollOnMount;

  const [overlay, originalHeader, body, footer, otherChildren] =
    extractSlots(children);

  const header = isValidElement(originalHeader)
    ? cloneElement(originalHeader as ReactElement<{ onClose?: () => void }>, {
        onClose:
          (originalHeader as ReactElement<{ onClose?: () => void }>).props
            .onClose ?? onClose,
      })
    : null;

  return (
    <Modal.BaseModal
      isOpen={isOpen}
      open={openValue}
      onClose={onClose}
      hideOverlay={hideOverlay}
      scrollBehaviour={scrollBehaviour}
      scrollBehavior={scrollBehavior}
      isCentered={isCentered}
      centered={centeredValue}
      placement={placement}
      closeOnEsc={closeOnEsc}
      closeOnEscape={closeOnEscValue}
      closeOnOverlayClick={closeOnOverlayClick}
      closeOnInteractOutside={closeOnOverlayValue}
      blockScrollOnMount={blockScrollOnMount}
      preventScroll={preventScrollValue}
      initialFocusRef={initialFocusRef}
      finalFocusRef={finalFocusRef}
      onOpenChange={onOpenChange}
      size={size}
      {...rest}
    >
      {!hideOverlay && (overlay ?? <Modal.Overlay />)}
      <Modal.Content
        borderRadius={size === "full" || size === "cover" ? "0" : "lg"}
        size={size}
        {...modalContentProps}
      >
        {header}

        {otherChildren}

        {isValidElement(body)
          ? cloneElement(body as ReactElement<ModalBodyProps>, {
              bg: bodyBackgroundColor(variant),
              hasFooter: Boolean(footer),
            })
          : body}

        {footer}
      </Modal.Content>
    </Modal.BaseModal>
  );
}

Modal.BaseModal = function BaseModal(
  props: ModalProps & { children: ReactNode }
): ReactElement {
  const {
    children,
    isOpen,
    open,
    onClose,
    hideOverlay,
    scrollBehaviour,
    scrollBehavior,
    isCentered,
    centered,
    closeOnEsc,
    closeOnEscape,
    closeOnOverlayClick,
    closeOnInteractOutside,
    blockScrollOnMount,
    preventScroll,
    placement,
    initialFocusRef,
    finalFocusRef,
    onOpenChange,
    size,
    modal,
    trapFocus,
    ...rest
  } = props;

  const openValue = open ?? isOpen;
  const centeredValue = centered ?? isCentered;
  const closeOnEscValue = closeOnEscape ?? closeOnEsc;
  const closeOnOverlayValue =
    closeOnInteractOutside ?? closeOnOverlayClick ?? true;
  const preventScrollValue = preventScroll ?? blockScrollOnMount;
  const placementValue = placement ?? (centeredValue ? "center" : undefined);
  const scrollBehaviorValue = scrollBehavior ?? scrollBehaviour;

  // Map unsupported sizes to "xl" for Chakra v3 compatibility
  // Chakra v3 supports: xs, sm, md, lg, xl, full, cover
  // Unsupported sizes: 2xl, 3xl, 4xl, 5xl, 6xl -> map to "xl" (not "full" to avoid full-screen styles)
  // Modal.Content handles the actual sizing via sizeToMaxWidth/sizeToMaxHeight
  const sizeValue =
    size === "2xl" ||
    size === "3xl" ||
    size === "4xl" ||
    size === "5xl" ||
    size === "6xl"
      ? "xl"
      : size;

  const { overlay, content, otherChildren } = extractBaseSlots(children);

  // Extract zIndex from content props to apply to Positioner
  // This ensures the positioner (and its content) renders above the overlay
  const contentZIndex = content?.props?.zIndex;
  const positionerStyle: CSSProperties = {
    pointerEvents: "auto",
    ...(contentZIndex && { zIndex: contentZIndex }),
  };

  return (
    <ChakraDialog.Root
      open={openValue}
      modal={modal}
      trapFocus={trapFocus}
      placement={placementValue}
      scrollBehavior={scrollBehaviorValue}
      closeOnEscape={closeOnEscValue}
      closeOnInteractOutside={closeOnOverlayValue}
      preventScroll={preventScrollValue}
      size={sizeValue}
      initialFocusEl={
        initialFocusRef ? () => initialFocusRef.current : undefined
      }
      finalFocusEl={finalFocusRef ? () => finalFocusRef.current : undefined}
      onOpenChange={(details: { open: boolean }) => {
        onOpenChange?.(details);
        if (!details.open) {
          onClose?.();
        }
      }}
      {...rest}
    >
      <Portal>
        {!hideOverlay && (overlay ?? <Modal.Overlay />)}
        <ChakraDialog.Positioner style={positionerStyle}>
          {content ? (
            cloneElement(content, { size, ...content.props })
          ) : (
            <Modal.Content size={size}>
              {otherChildren.length ? otherChildren : null}
            </Modal.Content>
          )}
          {content ? otherChildren : null}
        </ChakraDialog.Positioner>
      </Portal>
    </ChakraDialog.Root>
  );
};

Modal.Overlay = function Overlay(props: DialogBackdropProps): JSX.Element {
  return <ChakraDialog.Backdrop {...props} />;
};

// Map size names to explicit maxWidth values (Chakra v3 default sizes)
export const sizeToMaxWidth: Record<string, string> = {
  xs: "320px",
  sm: "384px",
  md: "448px",
  lg: "512px",
  xl: "576px",
  "2xl": "672px",
  "3xl": "768px",
  "4xl": "896px",
  "5xl": "1024px",
  "6xl": "1152px",
  cover: "100vw",
  full: "100vw",
};

// Map size names to explicit maxHeight values (matching Chakra v1 standards)
const sizeToMaxHeight: Record<string, string> = {
  xs: "calc(100vh - 8rem)",
  sm: "calc(100vh - 8rem)",
  md: "calc(100vh - 7.5rem)",
  lg: "calc(100vh - 7.5rem)",
  xl: "calc(100vh - 7rem)",
  "2xl": "calc(100vh - 6rem)",
  "3xl": "calc(100vh - 5rem)",
  "4xl": "calc(100vh - 4rem)",
  "5xl": "calc(100vh - 3rem)",
  "6xl": "calc(100vh - 2rem)",
  cover: "100dvh",
  full: "100dvh",
};

Modal.Content = function Content(
  props: ChakraDialogContentProps & { size?: string; css?: object }
): JSX.Element {
  const { size, css: cssProp, ...rest } = props;
  const maxWidth = size ? sizeToMaxWidth[size] : undefined;
  const maxHeight = size ? sizeToMaxHeight[size] : undefined;
  const isFullSize = size === "full" || size === "cover";

  return (
    <ChakraDialog.Content
      bg="bg"
      color="fg"
      css={{
        maxWidth: `${maxWidth} !important`,
        maxHeight: `${maxHeight} !important`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        // Ensure modal content is always clickable even if body has pointer-events: none
        pointerEvents: "auto",
        ...(isFullSize && {
          width: "100vw !important",
          height: "100dvh !important",
          maxHeight: "100dvh !important",
          borderRadius: "0",
          margin: "0",
        }),
        ...((cssProp as object) || {}),
      }}
      {...rest}
    />
  );
};

Modal.Header = function Header(
  props: ModalHeaderSharedProps & ChakraDialogHeaderProps
): JSX.Element {
  const { onClose, children, closeButtonProps, ...rest } = props;
  const { t } = useTranslation("common");

  const getCloseButton = () => {
    return (
      onClose && (
        <IconButton
          onClick={onClose}
          variant="ghost"
          iconName="X"
          aria-label={t("Close modal", { ns: "common" })}
          size="sm"
          iconSize="24px"
          data-testid="modal-close-button"
          colorPalette="link"
          iconColor={closeButtonProps?.iconColor ?? "fg"}
          {...closeButtonProps}
        />
      )
    );
  };

  return (
    <ChakraDialog.Header
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      lineHeight="30px"
      fontSize={{
        base: "md", // 16px
        sm: "xl", // 20px
      }}
      padding={{
        base: "16px 24px",
        sm: "16px 20px",
      }}
      position="relative"
      borderTopRadius="lg"
      color="fg"
      bg="bg"
      borderBottom="1px solid"
      borderColor="border"
      data-testid="modal-header"
      fontWeight="600"
      {...rest}
    >
      {children}
      {getCloseButton()}
    </ChakraDialog.Header>
  );
};

Modal.Footer = function Footer(props: ChakraDialogFooterProps): JSX.Element {
  return (
    <ChakraDialog.Footer
      borderTop="1px solid"
      borderColor="border"
      flexDirection="row-reverse"
      justifyContent="space-between"
      padding={{
        base: "16px 24px",
        sm: "16px 20px",
      }}
      {...props}
    />
  );
};

Modal.Body = forwardRef<HTMLDivElement, ModalBodyProps>(function Body(
  props,
  ref
): JSX.Element {
  const { hasFooter, padding, p, ...rest } = props;
  // Use passed padding/p if provided, otherwise use defaults
  const paddingValue =
    padding !== undefined || p !== undefined
      ? padding ?? p
      : {
          base: "16px 24px",
          sm: "24px",
        };
  return (
    <ChakraDialog.Body
      ref={ref}
      padding={paddingValue}
      fontSize="sm"
      flex="1"
      overflowY="auto"
      {...(!hasFooter && {
        borderBottomRadius: "lg",
      })}
      {...rest}
    />
  );
});

Modal.CloseButton = function ModalCloseButton(
  props: UiKitCloseButtonProps &
    Omit<ChakraDialogCloseTriggerProps, "asChild" | "children">
): JSX.Element {
  return (
    <ChakraDialog.CloseTrigger
      position="absolute"
      top="sm"
      insetEnd="3"
      {...props}
      asChild
    >
      <CloseButton tabIndex={-1} {...props} />
    </ChakraDialog.CloseTrigger>
  );
};
